// src/auth/AuthContext.js
import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';

export const AuthContext = createContext();

// I'll keep this for your testing, 0.1 minutes = 6 seconds
const LOGOUT_TIMER_MINUTES = 300;
const LOGOUT_TIMER_MS = LOGOUT_TIMER_MINUTES * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
    token: null,
  });

  const logoutTimer = useRef(null);

  // NEW: Wrap logout in useCallback to stabilize it.
  // The dependency array is empty because it only uses `setAuthState`
  // which is guaranteed by React to be stable.
  const logout = useCallback(() => {
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('user');
    setAuthState({ isAuthenticated: false, user: null, token: null });
    if (logoutTimer.current) {
        clearTimeout(logoutTimer.current);
    }
    console.log('Session expired. Logged out automatically.');
  }, []);

  // NEW: Wrap startLogoutTimer in useCallback.
  // It depends on `logout`, so we add `logout` to its dependency array.
  const startLogoutTimer = useCallback(() => {
    if (logoutTimer.current) {
        clearTimeout(logoutTimer.current);
    }

    logoutTimer.current = setTimeout(logout, LOGOUT_TIMER_MS);
  }, [logout]);


  useEffect(() => {
    const token = sessionStorage.getItem('authToken');
    const user = sessionStorage.getItem('user');

    if (token && user) {
      setAuthState({
        isAuthenticated: true,
        user: JSON.parse(user),
        token: token,
      });
      startLogoutTimer();
    }
  // NEW: Add startLogoutTimer as a dependency. Because it's wrapped in
  // useCallback, this effect will still only run on mount as intended.
  }, [startLogoutTimer]);


  const login = (userData, token) => {
    sessionStorage.setItem('authToken', token);
    sessionStorage.setItem('user', JSON.stringify(userData));

    setAuthState({
      isAuthenticated: true,
      user: userData,
      token: token,
    });
    
    startLogoutTimer();
  };

  // We are now using the useCallback version of logout for everything.
  // The manual logout function can simply be a call to it.
  const manualLogout = useCallback(() => {
    console.log('Logging out manually.');
    logout();
  }, [logout]);


  return (
    // We provide `manualLogout` as `logout` to the rest of the app.
    <AuthContext.Provider value={{ authState, login, logout: manualLogout }}>
      {children}
    </AuthContext.Provider>
  );
};